import {
  type SophiConfig,
  type SophiLocale,
  type UserData,
  type EventHandler,
  type EventName,
  type SophiEventMap,
  type IncomingMessage,
  type OutgoingMessage,
  type AddToCartEvent,
  type AddToFavoriteEvent,
  type IUserCart,
  type SessionData,
  type SessionDataChatPayload,
  type SetLocalePayload,
  type SetConsentPayload,
  type ProductSummary,
  type ProductReplyOptions,
  type OutfitResponse,
  type UpdateFavoritePayload,
  OutgoingMessageTypes,
} from "./types";
import { ApiService } from "./services/api.service";
import { ERROR_MESSAGES, API_BASE_URLS, APP_BASE_URLS } from "./constants/config";
import { validateConfig, validateUserCart, isValidAddToCartMessage, parseAddToFavoriteMessage } from "./validations";

/**
 * Main Sophi Widget SDK class
 * Provides a framework-agnostic way to embed and interact with the Sophi AI widget
 */
export class SophiWidget {
  private config: SophiConfig | null = null;
  private iframe: HTMLIFrameElement | null = null;
  private container: HTMLElement | null = null;
  private eventListeners: Map<EventName, Set<EventHandler<any>>> = new Map();
  private messageHandler: ((event: MessageEvent) => void) | null = null;
  private isInitialized = false;
  private iframeOrigin: string | null = null;
  private sessionData: SessionData | null = null;
  private currentLocale: SophiLocale | undefined = undefined;
  private apiService: ApiService | null = null;
  private apiBaseUrl: string | null = null;
  /**
   * Whether the iframe app has acknowledged receiving session_data and is
   * fully ready to handle incoming messages. Set to true when the iframe
   * emits the `ready` postMessage. Reset on destroy().
   */
  private iframeAppReady = false;
  /**
   * Messages queued while the iframe app is still initializing (before `ready`).
   * Flushed in FIFO order when `ready` fires.
   */
  private pendingMessages: OutgoingMessage[] = [];

  /**
   * Initialize the widget with configuration
   * @param config - Widget configuration
   */
  public async init(config: SophiConfig): Promise<void> {
    if (this.isInitialized) {
      console.warn(ERROR_MESSAGES.ALREADY_INITIALIZED);
      return;
    }

    try {
      // Validate configuration
      validateConfig(config);

      this.config = this.formatConfig(config);
      this.currentLocale = this.config.locale;

      // Determine API URL based on environment
      const apiBaseUrl = API_BASE_URLS[config.environment || "production"] || API_BASE_URLS.production;
      this.apiBaseUrl = apiBaseUrl;

      // Initialize API service. storageConsent defaults to true (persistent);
      // pass false to run anonymously (no cookies, fresh session each load).
      this.apiService = new ApiService(this.apiBaseUrl, config.apiKey, config.userId, config.storageConsent !== false);

      // Create authentication session
      await this.createAuthSession();

      this.isInitialized = true;

      // Extract origin from iframe URL for security
      try {
        const url = new URL(this.getIframeUrl());
        this.iframeOrigin = url.origin;
      } catch (error) {
        const err = new Error(`${ERROR_MESSAGES.INVALID_IFRAME_URL}: ${this.getIframeUrl()}`);
        this.emitError(err);
        throw err;
      }

      // Resolve container element
      this.container = this.resolveContainer(config.container);

      if (!this.container) {
        const err = new Error(ERROR_MESSAGES.CONTAINER_NOT_FOUND);
        this.emitError(err);
        throw err;
      }

      // Create and mount iframe
      this.createIframe();

      // Setup postMessage listener
      this.setupMessageListener();

      // Send session data to iframe after it's loaded
      this.iframe?.addEventListener("load", () => {
        // Small delay to ensure iframe is fully ready
        setTimeout(() => {
          this.sendSessionData();
          if (this.config?.layout) {
            this.sendLayoutConfig();
          }
        }, 100);
      });

      // Call onReady callback if provided
      if (config.onReady) {
        config.onReady();
      }
    } catch (error) {
      // Reset state on error
      this.isInitialized = false;
      this.config = null;
      this.apiService = null;
      this.sessionData = null;
      this.currentLocale = undefined;

      const err = error instanceof Error ? error : new Error(String(error));
      this.emitError(err);
      throw err;
    }
  }

  /**
   * Send user data to the widget
   * @param data - User data object
   */
  public sendUserData(data: UserData): void {
    if (!this.isInitialized || !this.iframe) {
      console.warn("Widget not initialized. Call init() first.");
      return;
    }

    const message: OutgoingMessage = {
      type: OutgoingMessageTypes.USER_DATA,
      data,
    };

    this.postMessage(message);
  }

  /**
   * Toggle chat history visibility in the iframe child app.
   */
  public sendToggleHistory(): void {
    if (!this.isInitialized || !this.iframe) {
      console.warn("Widget not initialized. Call init() first.");
      return;
    }

    const message: OutgoingMessage = {
      type: OutgoingMessageTypes.TOGGLE_HISTORY,
    };

    this.postMessage(message);
  }

  /**
   * Send a plain chat message to the widget with no product or outfit context.
   *
   * @param query - The text query to send.
   *
   * @example
   * sdk.sendTextMessage('What should I wear today?')
   */
  public sendTextMessage(query: string): void {
    if (!this.iframe || !this.iframe.contentWindow) {
      console.warn("[Sophi] sendTextMessage: iframe is not ready.");
      return;
    }

    const message: OutgoingMessage = {
      type: OutgoingMessageTypes.SEND_TEXT_MESSAGE,
      data: { query },
    };

    if (!this.iframeAppReady) {
      this.pendingMessages.push(message);
      return;
    }

    this.postMessage(message);
  }

  /**
   * Send a chat message scoped to a specific product card.
   *
   * `productExternalId` is the external id of the product the user is asking about.
   * Supply `opts.query` / `opts.isOutfit` when the product belongs to an outfit
   * recommendation or the query involves outfit context.
   *
   * @param externalProductId - The product external id the user is asking about.
   * @param opts    - Optional query and outfit context.
   *
   * @example
   * sdk.sendProductReply('Does this come in blue?', '1234567890')
   * sdk.sendProductReply('1234567890', { query: 'Tell me about this jacket', isOutfit: true })
   */
  public sendProductReply(externalProductId: string, opts?: ProductReplyOptions): void {
    if (!this.iframe || !this.iframe.contentWindow) {
      console.warn("[Sophi] sendProductReply: iframe is not ready.");
      return;
    }

    const message: OutgoingMessage = {
      type: OutgoingMessageTypes.SEND_PRODUCT_REPLY,
      data: {
        query: opts?.query || undefined,
        externalProductId,
        ...(opts?.isOutfit !== undefined && { isOutfit: opts.isOutfit }),
      },
    };

    if (!this.iframeAppReady) {
      this.pendingMessages.push(message);
      return;
    }

    this.postMessage(message);
  }

  /**
   * Send a chat message scoped to a full outfit recommendation.
   *
   * `outfitId` is derived automatically from `outfit.recommendationId` — do not pass it separately.
   *
   * @param query  - The text query to send.
   * @param outfit - The outfit recommendation the user is asking about.
   *
   * @example
   * sdk.sendOutfitReply('Can you style this differently?', outfit)
   */
  public sendOutfitReply(query: string, outfit: OutfitResponse): void {
    if (!this.iframe || !this.iframe.contentWindow) {
      console.warn("[Sophi] sendOutfitReply: iframe is not ready.");
      return;
    }

    const message: OutgoingMessage = {
      type: OutgoingMessageTypes.SEND_OUTFIT_REPLY,
      data: {
        query,
        outfitId: outfit.recommendationId,
        outfit,
      },
    };

    if (!this.iframeAppReady) {
      this.pendingMessages.push(message);
      return;
    }

    this.postMessage(message);
  }

  /**
   * Instruct the iframe to fetch and open the product detail panel for the given
   * product ID. This is equivalent to the user clicking a product card in the
   * chat results — the iframe resolves the full product and navigates to the
   * detail view itself.
   *
   * @param externalProductId - The product external id to open.
   * @param reason    - Optional recommendation reason text shown with the product.
   *
   * @example
   * sdk.openProductById('1234567890')
   * sdk.openProductById('1234567890', 'Matches your style preferences')
   */
  public openProductById(externalProductId: string, reason?: string): void {
    if (!this.iframe || !this.iframe.contentWindow) {
      console.warn("[Sophi] openProductById: iframe is not ready.");
      return;
    }

    const message: OutgoingMessage = {
      type: OutgoingMessageTypes.OPEN_PRODUCT_BY_ID,
      data: {
        externalProductId,
        ...(reason !== undefined && { reason }),
      },
    };

    this.postMessage(message);
  }

  /**
   * Notifies the iframe that a product's favorite state has changed from outside
   * (e.g. the host app's ProductCard toggled the favorite). Only the matching
   * AddToFavorite component inside the iframe will re-render.
   *
   * @param productId - String product ID (matches the product's `id` in the iframe)
   * @param isFavorite - New favorite state
   * @example
   * sdk.updateFavorite('42', false)
   */
  public updateFavorite(productId: string, isFavorite: boolean): void {
    if (!this.iframe || !this.iframe.contentWindow) {
      console.warn("[Sophi] updateFavorite: iframe is not ready.");
      return;
    }

    const message: OutgoingMessage = {
      type: OutgoingMessageTypes.UPDATE_FAVORITE,
      data: { productId, isFavorite } satisfies UpdateFavoritePayload,
    };

    this.postMessage(message);
  }

  /**
   * Re-posts `session_data` to the chat iframe when a session exists and the iframe can receive messages.
   * Use after host-side session refresh or if the child app missed the initial payload.
   */
  public sendSessionToChat(): void {
    if (!this.isInitialized || !this.iframe) {
      console.warn("[Sophi] sendSessionToChat: widget not initialized.");
      return;
    }
    this.sendSessionData();
  }

  /**
   * Update storage (cookie) consent at runtime without losing the open chat.
   *
   * When granting consent, the current in-memory session is persisted to
   * cookies. For a logged-in user (a userId was supplied at init), the guest
   * session is merged into that user via `/auth/v1/client/update` and a fresh
   * session is opened for the user so the SDK receives their auth data; the new
   * session data is then re-posted to the iframe. The chat iframe is also told
   * to promote its in-memory store to localStorage so the conversation survives
   * the next reload. Withdrawing consent clears cookies and demotes the iframe
   * back to memory-only.
   *
   * @param granted - `true` to enable persistence, `false` to withdraw it.
   */
  public async setStorageConsent(granted: boolean): Promise<void> {
    if (this.config) {
      this.config.storageConsent = granted;
    }

    if (!this.apiService) {
      console.warn("[Sophi] setStorageConsent: widget not initialized yet.");
      return;
    }

    if (granted) {
      try {
        // For a logged-in user this returns the newly opened session (merge +
        // new session); for a guest it returns null and the existing session
        // simply becomes persistent.
        const newSession = await this.apiService.enableStorageConsent({
          metadata: this.config?.metadata,
        });
        if (newSession) {
          this.sessionData = newSession;
        }
      } catch (error) {
        this.emitError(error instanceof Error ? error : new Error(String(error)));
      }
      this.postSetConsent(true);
      // Re-send session so the iframe receives the (possibly new) session data.
      this.sendSessionData();
    } else {
      this.apiService.disableStorageConsent();
      this.postSetConsent(false);
    }
  }

  /**
   * Tell the chat iframe to switch its persistence strategy at runtime.
   */
  private postSetConsent(storage: boolean): void {
    if (!this.iframe || !this.iframe.contentWindow) {
      return;
    }

    const message: OutgoingMessage = {
      type: OutgoingMessageTypes.SET_CONSENT,
      data: { storage } satisfies SetConsentPayload,
    };

    this.postMessage(message);
  }

  /**
   * Posts `set_locale` so the iframe can switch language without a full `session_data` round trip.
   *
   * @param locale - `'tr'` | `'en'` | `'pt'`
   */
  public setLocale(locale: SophiLocale): void {
    if (locale !== "tr" && locale !== "en" && locale !== "pt") {
      console.warn(`[Sophi] setLocale: invalid locale "${locale}" — expected "tr", "en" or "pt".`);
      return;
    }
    this.currentLocale = locale;

    if (!this.iframe?.contentWindow) {
      console.warn("[Sophi] setLocale: iframe is not ready.");
      return;
    }

    const message: OutgoingMessage = {
      type: OutgoingMessageTypes.SET_LOCALE,
      data: { locale } satisfies SetLocalePayload,
    };

    this.postMessage(message);
  }

  public updateUserCart(cart: IUserCart): void {
    if (!this.isInitialized || !this.iframe) {
      console.warn("Widget not initialized. Call init() first.");
      return;
    }

    validateUserCart(cart);

    const message: OutgoingMessage = {
      type: OutgoingMessageTypes.UPDATE_USER_CART,
      data: cart,
    };

    this.postMessage(message);
  }

  private onDestroy(): void {
    if (!this.isInitialized || !this.iframe) {
      console.warn("Widget not initialized. Call init() first.");
      return;
    }

    const message: OutgoingMessage = {
      type: OutgoingMessageTypes.DESTROY,
    };

    this.postMessage(message);
  }

  /**
   * Register an event listener
   * @param event - Event name
   * @param handler - Event handler function
   */
  public on<K extends EventName>(event: K, handler: EventHandler<SophiEventMap[K]>): void {
    if (!this.eventListeners.has(event)) {
      this.eventListeners.set(event, new Set());
    }
    this.eventListeners.get(event)!.add(handler);
  }

  /**
   * Unregister an event listener
   * @param event - Event name
   * @param handler - Event handler function to remove
   */
  public off<K extends EventName>(event: K, handler: EventHandler<SophiEventMap[K]>): void {
    const handlers = this.eventListeners.get(event);
    if (handlers) {
      handlers.delete(handler);
    }
  }

  /**
   * Show the widget
   */
  public show(): void {
    if (this.iframe) {
      this.iframe.style.display = "block";
    }
  }

  /**
   * Hide the widget
   */
  public hide(): void {
    if (this.iframe) {
      this.iframe.style.display = "none";
    }
  }

  /**
   * Destroy the widget and cleanup resources
   * Waits 300ms after sending destroy message to give iframe time to cleanup
   */
  public async destroy(): Promise<void> {
    this.onDestroy();

    // Wait 300ms to give iframe time to process the destroy event and cleanup
    await new Promise((resolve) => setTimeout(resolve, 300));

    // Remove message listener
    if (this.messageHandler) {
      window.removeEventListener("message", this.messageHandler);
      this.messageHandler = null;
    }

    // Remove iframe
    if (this.iframe && this.iframe.parentNode) {
      this.iframe.parentNode.removeChild(this.iframe);
      this.iframe = null;
    }

    // Clear event listeners
    this.eventListeners.clear();

    // Reset state
    this.config = null;
    this.container = null;
    this.isInitialized = false;
    this.iframeOrigin = null;
    this.sessionData = null;
    this.currentLocale = undefined;
    this.apiService = null;
    this.iframeAppReady = false;
    this.pendingMessages = [];
  }

  /**
   * Check if widget is initialized
   */
  public isReady(): boolean {
    return this.isInitialized && this.iframe !== null;
  }

  /**
   * Format configuration
   */
  private formatConfig(config: SophiConfig): SophiConfig {
    return {
      ...config,
      userId: config.userId || undefined,
    };
  }

  /**
   * Create authentication session with the API
   */
  private async createAuthSession(): Promise<void> {
    if (!this.config || !this.apiService) {
      throw new Error(ERROR_MESSAGES.NOT_INITIALIZED);
    }

    try {
      this.sessionData = await this.apiService.createSession({
        externalUserId: this.config.userId,
        metadata: this.config.metadata,
      });

      // Validate session data
      if (!this.apiService.validateSessionData(this.sessionData)) {
        throw new Error(ERROR_MESSAGES.INVALID_SESSION_DATA);
      }
    } catch (error) {
      const err = error instanceof Error ? error : new Error(ERROR_MESSAGES.SESSION_CREATION_FAILED);
      throw err;
    }
  }

  /** Non-empty display name for `session_data`, or `""` (never `undefined`). */
  private resolveSessionUserName(fromConfig?: string, fromSession?: string): string {
    const pick = [fromConfig, fromSession].find((v) => typeof v === "string" && v.trim().length > 0);
    return pick ? pick.trim() : "";
  }

  /**
   * Build the `session_data` payload for the chat iframe.
   */
  private buildChatSessionPayload(): SessionDataChatPayload | null {
    if (!this.sessionData || !this.config) {
      return null;
    }

    const s = this.sessionData;
    const layoutFromApi = s.layoutId;
    const layoutResolved = layoutFromApi !== undefined && layoutFromApi !== null && String(layoutFromApi).length > 0 ? String(layoutFromApi) : undefined;

    const userName = this.resolveSessionUserName(this.config.userName, s.userName);

    const payload: SessionDataChatPayload = {
      accessToken: s.accessToken,
      sessionId: s.sessionId,
      clientId: s.clientId,
      companyCode: s.companyCode,
      expiresAt: s.expiresAt,
      userName,
      ...(layoutResolved !== undefined ? { layoutId: layoutResolved } : {}),
      ...(this.currentLocale ? { locale: this.currentLocale } : {}),
    };

    return payload;
  }

  /**
   * Send session data to iframe (`session_data`).
   */
  private sendSessionData(): void {
    const data = this.buildChatSessionPayload();
    if (!data) {
      console.warn("Session data not available");
      return;
    }

    const message: OutgoingMessage = {
      type: OutgoingMessageTypes.SESSION_DATA,
      data,
    };

    this.postMessage(message);
  }

  /**
   * Send layout config to iframe so the child app can apply layout options (e.g. show/hide header).
   */
  private sendLayoutConfig(): void {
    if (!this.config?.layout) {
      return;
    }

    const message: OutgoingMessage = {
      type: OutgoingMessageTypes.LAYOUT_CONFIG,
      data: this.config.layout,
    };

    this.postMessage(message);
  }

  /**
   * Resolve container element from selector or element
   */
  private resolveContainer(container: string | HTMLElement): HTMLElement | null {
    if (typeof container === "string") {
      return document.querySelector(container);
    }
    return container;
  }

  /**
   * Create and mount the iframe
   */
  private createIframe(): void {
    if (!this.container || !this.config) {
      return;
    }

    const iframe = document.createElement("iframe");

    // Set iframe attributes
    iframe.src = this.buildIframeUrl();
    iframe.style.width = this.config.width || "100%";
    iframe.style.height = this.config.height || "600px";
    iframe.style.border = "none";
    iframe.title = "Sophi AI Widget";
    iframe.allow = "microphone; camera; clipboard-write";

    // Append to container
    this.container.appendChild(iframe);
    this.iframe = iframe;
  }

  /**
   * Build iframe URL with query parameters
   */
  private buildIframeUrl(): string {
    if (!this.config) {
      return "";
    }
    const url = new URL(this.getIframeUrl());

    // Tell the chat app to start in anonymous (memory-only) mode so it can
    // decide its persistence strategy at boot, before Pinia hydrates.
    if (this.config.storageConsent === false) {
      url.searchParams.set("storageConsent", "0");
    }

    return url.toString();
  }

  private getIframeUrl(): string {
    if (!this.config) {
      return "";
    }
    if (this.config.environment === "production") {
      return APP_BASE_URLS.production;
    }
    return APP_BASE_URLS.test;
  }

  /**
   * Setup postMessage listener for iframe communication
   */
  private setupMessageListener(): void {
    this.messageHandler = (event: MessageEvent) => {
      // Validate origin for security
      if (this.iframeOrigin && event.origin !== this.iframeOrigin) {
        console.warn(`Received message from untrusted origin: ${event.origin}`);
        return;
      }

      try {
        // Parse message
        const message: IncomingMessage = typeof event.data === "string" ? JSON.parse(event.data) : event.data;

        // Handle different message types
        this.handleIncomingMessage(message);
      } catch (error) {
        console.error("Error processing message from iframe:", error);
      }
    };

    window.addEventListener("message", this.messageHandler);
  }

  /**
   * Handle incoming messages from iframe
   */
  private handleIncomingMessage(message: IncomingMessage): void {
    switch (message.type) {
      case "add_to_cart":
        if (isValidAddToCartMessage(message.data)) {
          this.emit("add_to_cart", message.data);
        } else {
          console.error("Invalid add_to_cart message structure:", message.data);
          this.emitError(new Error("Invalid add_to_cart message format"));
        }
        break;

      case "add_to_favorite": {
        const favoriteResult = parseAddToFavoriteMessage(message.data);
        if (favoriteResult) {
          console.log(`[Sophi] add_to_favorite fired: productIdentifier=${favoriteResult.productIdentifier}, isFavorite=${favoriteResult.isFavorite}`);
          this.emit("add_to_favorite", favoriteResult);
        }
        break;
      }

      case "send_to_checkout":
        this.emit("send_to_checkout", undefined);
        break;
      case "ready":
        this.emit("ready", undefined);
        // Guard: only flush queued messages once. Subsequent `ready` events
        // (e.g. from session_data re-sends) must not re-flush or re-send session.
        if (!this.iframeAppReady) {
          this.iframeAppReady = true;
          // Flush any messages queued before the iframe app was ready (e.g.
          // sendTextMessage called immediately after init() — the M&S race condition).
          if (this.pendingMessages.length > 0) {
            const queued = this.pendingMessages.splice(0);
            queued.forEach((msg) => this.postMessage(msg));
          }
        }
        break;

      case "error":
        const error = message.data instanceof Error ? message.data : new Error(String(message.data));
        this.emitError(error);
        break;

      default:
        console.warn("Unknown message type:", message);
    }
  }

  /**
   * Post message to iframe
   */
  private postMessage(message: OutgoingMessage): void {
    if (!this.iframe || !this.iframe.contentWindow || !this.iframeOrigin) {
      console.warn("Cannot send message: iframe not ready");
      return;
    }

    try {
      this.iframe.contentWindow.postMessage(message, this.iframeOrigin);
    } catch (error) {
      console.error("Error sending message to iframe:", error);
      this.emitError(error instanceof Error ? error : new Error(String(error)));
    }
  }

  /**
   * Emit event to registered listeners
   */
  private emit<K extends EventName>(event: K, data: SophiEventMap[K]): void {
    const handlers = this.eventListeners.get(event);
    if (handlers) {
      handlers.forEach((handler) => {
        try {
          handler(data);
        } catch (error) {
          console.error(`Error in ${event} event handler:`, error);
        }
      });
    }
  }

  /**
   * Emit error event
   */
  private emitError(error: Error): void {
    this.emit("error", error);

    // Also call onError callback if provided
    if (this.config?.onError) {
      this.config.onError(error);
    }
  }
}
